<template>
  <BContainer>
    <BRow align-v="center">
      <BCol lg="6" order-lg="2" class="pl-lg-5 text-right">
        <cld-image public-id="v1557870188/state_of_visual_media/cloudinary-slide4.svg" alt="A drone delivering a piece of media" fluid />
        <h2 class="my-4">There’s more to visual impact than the visual itself</h2>
        <p>One of the essential traits of a great story is a delivery that creates an engaging experience—and visual storytelling is no different.</p>
        <v-popover popover-class="pp-20">
          <LearnMore/>
          <template slot="popover">
            <ChartDonut :dark="true" :num="numbers.tooltip.slow" class="chart-donut ml-4" chart-id="ChartDonut-slow" />
            <div class="popover-title" style="margin: 0;">
              <cld-image class="tooltip-finger-icon" public-id="v1557870041/state_of_visual_media/cloudinary-finger.svg" alt="Icon of a hand with the index finger pointing up, representing a tool tip" />
              <p>
                Nearly
                <strong>{{ numbers.tooltip.slow }}%</strong> of shoppers are likely to abandon slow sites*
              </p>
            </div>
            <small class="d-block ml-4 mt-4">*Mobify 2017 Q2 Mobile Commerce Insights</small>
          </template>
        </v-popover>
      </BCol>
      <BCol lg="6" class="pr-lg-5">
        <p>Delivering compelling visuals quickly and completely is critical to achieving the desired visual hook—and a large percentage of visual media could be further optimized to capture this experience.</p>
        <v-popover popover-class="pp-20">
          <LearnMore/>
          <template slot="popover">
            <ChartDonut :dark="true" :num="numbers.tooltip.consistent" class="chart-donut ml-4" chart-id="ChartDonut-consistent" />
            <div class="popover-title" style="margin: 0;">
              <cld-image class="tooltip-finger-icon" public-id="v1557870041/state_of_visual_media/cloudinary-finger.svg" alt="Icon of a hand with the index finger pointing up, representing a tool tip" />
              <p>
                <strong>{{ numbers.tooltip.consistent }}%</strong> of consumers expect a consistent experience wherever they engage — website, social media, mobile, in person
              </p>
            </div>
          </template>
        </v-popover>
        <strong class="d-block mt-5">Different industries make different trade-offs for image optimization</strong>
        <ul>
          <!-- prettier-ignore -->
          <li>
            Agriculture:
            <strong>{{ kilobytes(numbers.Agriculture) }}</strong> (median JPEG size)
          </li>
          <!-- prettier-ignore -->
          <li>
            Automotive:
            <strong>{{ kilobytes(numbers.Automotive) }}</strong> (median JPEG size)
          </li>
          <!-- prettier-ignore -->
          <li>
            Real Estate:
            <strong>{{ kilobytes(numbers["Real Estate"]) }}</strong> (median JPEG size)
          </li>
        </ul>
        <v-popover popover-class="pp-100" placement="right">
          <LearnMore/>
          <template slot="popover">
            <div class="popover-title title-insg" style="margin: 0;">
              <aside>
                <cld-image class="tooltip-finger-icon" public-id="v1557870041/state_of_visual_media/cloudinary-finger.svg" alt="Icon of a hand with the index finger pointing up, representing a tool tip" />
              </aside>
              <aside>
                <strong>A closer look about the increasing use of visual media…</strong>
                <p>Over the course of a day, users engage most with food-industry videos during peak meal hours</p>
              </aside>
            </div>
            <div class="ml-sm-5">
              <ul class="chart-legend d-flex">
                <li>India</li>
                <li>Singapore</li>
              </ul>
              <ChartLine :chartdata="chartableIndiaSingapore" :dark="true" :height="chartHeight" chart-id="line-IndiaSingapore" />
            </div>
          </template>
        </v-popover>
      </BCol>
    </BRow>
    <footer>
      <p class="small">Sources: WOW Local Marketing, Mobify, Salesforce</p>
    </footer>
  </BContainer>
</template>

<script>
import LearnMore from "../LearnMore.vue";
import ChartDonut from "../donut-filled.vue";
import ChartLine from "../line.vue";

export default {
  components: {
    LearnMore,
    ChartDonut,
    ChartLine
  },
  props: {
    numbers: {
      type: Object,
      default: () => {
        return {};
      }
    },
    dataIndiaSingapore: {
      type: [Object, Array],
      default: () => [0]
    }
  },
  computed: {
    chartableIndiaSingapore() {
      let india = Object.values(this.dataIndiaSingapore[0]);
      india.splice(-1, 1);
      india = india.map(function(v) {
        return v * 100;
      });
      // console.log(india);
      let singapore = Object.values(this.dataIndiaSingapore[1]);
      singapore.splice(-1, 1);
      singapore = singapore.map(function(v) {
        return v * 100;
      });
      // console.log(singapore);
      const color1 = "#0091ff";
      const color2 = "#f4b21b";
      let transformed = {
        datasets: [
          {
            label: "India",
            data: india,
            backgroundColor: `rgba(${this.hexToRgb(color1)}, 0.75)`,
            borderColor: color1,
            pointBorderColor: color1,
            pointRadius: 0,
            borderWidth: 2,
            fill: true
            // data: this.dataDesktopMobile[0],
          },
          {
            label: "Singapore",
            data: singapore,
            backgroundColor: `rgba(${this.hexToRgb(color2)}, 0.75)`,
            borderColor: color2,
            pointBorderColor: color2,
            pointRadius: 0,
            borderWidth: 2,
            fill: true
            // data: this.dataDesktopMobile[1],
          }
        ],
        labels: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24]
      };

      this.dataDesktopMobile;

      return transformed;
    },
    chartHeight() {
      const winWidth = window.innerWidth;
      if (winWidth > 500) {
        return 360;
      }
      return 240;
    }
  },
  mounted: function() {},
  methods: {
    kilobytes(val) {
      return `${val}KiB`;
    }
  }
};
</script>

<style lang="scss" scoped>
ul {
  margin: 1rem 0;
  li {
    margin: 1rem 0;
  }
}
.popover-title.title-insg {
  /* display: block; */
  @include media-breakpoint-up(lg) {
    min-width: 600px;
  }
  @include media-breakpoint-up(xl) {
    min-width: 800px;
  }
}

ul.chart-legend {
  list-style: none;
  margin: 0;
  margin-top: 0.5em;
  padding: 0;
  li {
    margin-right: 2em;
    &:before {
      $dim: 12px;
      height: $dim;
      width: $dim;
      display: inline-block;
      margin-right: 0.25em;
      content: "";
    }
    &:nth-of-type(1):before {
      background: $blue;
    }
    &:nth-of-type(2):before {
      background: $gold;
    }
  }
}
</style>
